/**
 * Product Add-On Manager v9
 *
 * Handles two jobs:
 * A) On kit product pages — add selected add-ons alongside the kit when
 * the shopper clicks Add-to-Cart, and update the cart notification
 * popup to show all added items (main + add-ons) with the correct count.
 * B) Everywhere — when the main/parent kit is removed from the cart,
 * also remove its linked add-ons immediately without a page refresh.
 * If only the add-on is removed, the main product stays untouched.
 *
 * Line-item properties used
 *   _parent_kit_id : String(product_id) of the parent kit
 *   _is_addon      : "true"
 */

(function () {
  'use strict';

  var PROP_PARENT = '_parent_kit_id';
  var PROP_MARKER = '_is_addon';

  var _enforcing = false;
  var _lastCartToken = '';

  /* ── helpers ─────────────────────────────────────────────────────────── */

  function root() {
    try { return window.Shopify.routes.root || '/'; } catch (e) { return '/'; }
  }

  function realFetch(url, opts) {
    var fn = window.__addonRealFetch || window.fetch;
    return fn.call(window, url, opts);
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector || '.shopify-section').innerHTML;
  }

  function updateSection(id, html) {
    if (!html) return;
    var inner = getSectionInnerHTML(html);

    var elPrefixed = document.getElementById('shopify-section-' + id);
    if (elPrefixed) elPrefixed.innerHTML = html;

    var elBare = document.getElementById(id);
    if (elBare) {
      // For cart sections, only update .js-contents to avoid duplicating the title/header
      var jsContents = elBare.querySelector('.js-contents');
      if (jsContents) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newJsContents = parsed.querySelector('.js-contents');
        if (newJsContents) { jsContents.innerHTML = newJsContents.innerHTML; }
      } else {
        elBare.innerHTML = inner;
      }
    }

    if (id === 'cart-icon-bubble') {
      document.querySelectorAll('cart-icon-bubble').forEach(function (el) {
        var d = document.createElement('div');
        d.innerHTML = html;
        if (d.firstElementChild) el.innerHTML = d.firstElementChild.innerHTML;
      });
    }
  }

  function fetchAndApplySections(ids) {
    return realFetch(root() + '?sections=' + ids.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        ids.forEach(function (id) { if (data[id]) updateSection(id, data[id]); });
      })
      .catch(function () {});
  }

  function refreshSections() {
    fetchAndApplySections([
      'cart-drawer',
      'cart-icon-bubble',
      'main-cart-items',
      'main-cart-footer'
    ]).then(function () {
      realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var isEmpty = cart.item_count === 0;
          var cartItemsEl = document.querySelector('cart-items');
          var cartFooterEl = document.getElementById('main-cart-footer');
          if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', isEmpty);
          if (cartFooterEl) cartFooterEl.classList.toggle('is-empty', isEmpty);
          if (isEmpty) {
            document.querySelectorAll('#addon-only-cart-warning').forEach(function (el) { el.remove(); });
            var checkoutBtns = document.querySelectorAll('[name="checkout"], button#checkout, .cart__checkout-button');
            checkoutBtns.forEach(function (btn) {
              btn.disabled = false;
              btn.removeAttribute('aria-disabled');
              btn.style.opacity = '';
              btn.style.cursor = '';
              btn.style.pointerEvents = '';
            });
          }
        })
        .catch(function () {});
    }).catch(function () {});
  }

  function refreshNotificationWithAddons() {
    realFetch(root() + '?sections=cart-notification-product,cart-notification-button,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data['cart-notification-product']) {
          var parsed = new DOMParser().parseFromString(
            data['cart-notification-product'], 'text/html'
          );
          var itemDivs = parsed.querySelectorAll('[id^="cart-notification-product-"]');
          if (itemDivs.length > 0) {
            var container = document.getElementById('cart-notification-product');
            if (container) {
              container.innerHTML = '';
              itemDivs.forEach(function (div) {
                container.appendChild(document.importNode(div, true));
              });
            }
          }
        }
        if (data['cart-notification-button']) {
          updateSection('cart-notification-button', data['cart-notification-button']);
        }
        if (data['cart-icon-bubble']) {
          updateSection('cart-icon-bubble', data['cart-icon-bubble']);
        }
      })
      .catch(function () {});
  }

  /* ── ENFORCEMENT — remove orphaned add-ons (safety net) ─────────────── */

  function enforce() {
    if (_enforcing) return;
    _enforcing = true;

    realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.token === _lastCartToken) { _enforcing = false; return; }
        _lastCartToken = cart.token;

        var kitIds = {};
        var orphans = [];

        cart.items.forEach(function (it) {
          if (!it.properties || it.properties[PROP_MARKER] !== 'true') {
            kitIds[String(it.product_id)] = true;
          }
        });

        cart.items.forEach(function (it) {
          if (
            it.properties &&
            it.properties[PROP_MARKER] === 'true' &&
            it.properties[PROP_PARENT] &&
            !kitIds[String(it.properties[PROP_PARENT])]
          ) {
            orphans.push(it.key);
          }
        });

        var overQty = [];
        cart.items.forEach(function (it) {
          if (
            it.properties &&
            it.properties[PROP_MARKER] === 'true' &&
            it.quantity > 1
          ) {
            overQty.push(it.key);
          }
        });

        if (orphans.length === 0 && overQty.length === 0) { _enforcing = false; return; }

        var chain = Promise.resolve();
        orphans.forEach(function (key) {
          chain = chain.then(function () {
            return realFetch(root() + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          });
        });
        overQty.forEach(function (key) {
          chain = chain.then(function () {
            return realFetch(root() + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 1 })
            });
          });
        });
        chain
          .then(function () { _enforcing = false; _lastCartToken = ''; refreshSections(); })
          .catch(function () { _enforcing = false; });
      })
      .catch(function () { _enforcing = false; });
  }

  /* ── DOM-level co-removal ────────────────────────────────────────────── */

  var _pendingUnsub = null;

  function onRemoveButtonClick(event) {
    var btn = event.target.closest('cart-remove-button');
    if (!btn) return;

    var lineIndex = parseInt(btn.dataset.index, 10);
    if (isNaN(lineIndex)) return;

    var snapshotPromise = realFetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = cart.items[lineIndex - 1];
        if (!item) return null;
        if (item.properties && item.properties[PROP_MARKER] === 'true') return null;
        return String(item.product_id);
      })
      .catch(function () { return null; });

    if (_pendingUnsub) { _pendingUnsub(); _pendingUnsub = null; }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      var unsubFn = subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        if (_pendingUnsub) { _pendingUnsub(); _pendingUnsub = null; }

        snapshotPromise.then(function (parentId) {
          if (!parentId) return;

          realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var updates = {};
              var found = false;
              cart.items.forEach(function (it) {
                if (
                  it.properties &&
                  it.properties[PROP_MARKER] === 'true' &&
                  String(it.properties[PROP_PARENT]) === parentId
                ) {
                  updates[it.key] = 0;
                  found = true;
                }
              });
              if (!found) return;

              realFetch(root() + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates: updates })
              })
                .then(function () { _lastCartToken = ''; refreshSections(); })
                .catch(function () { _lastCartToken = ''; setTimeout(enforce, 500); });
            })
            .catch(function () { _lastCartToken = ''; setTimeout(enforce, 500); });
        });
      });

      _pendingUnsub = unsubFn;
    } else {
      snapshotPromise.then(function (parentId) {
        if (parentId) { _lastCartToken = ''; setTimeout(enforce, 800); }
      });
    }
  }

  function installRemoveListener() {
    document.addEventListener('click', onRemoveButtonClick, true);
  }

  /* ── Product page initialisation ────────────────────────────────────── */

  function initProductPage() {
    var s = document.getElementById('ProductAddonSection');
    if (!s) return;
    var pid = s.dataset.parentProductId;
    realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cart.items.forEach(function (it) {
          if (it.properties && String(it.properties[PROP_PARENT]) === String(pid)) {
            var cb = s.querySelector('.product-addon__checkbox[data-variant-id="' + it.variant_id + '"]');
            if (cb) cb.checked = true;
          }
        });
      })
      .catch(function () {});
  }

  function readCheckedAddons() {
    var s = document.getElementById('ProductAddonSection');
    if (!s) return [];
    var pid = s.dataset.parentProductId;
    if (!pid) return [];
    var out = [];
    s.querySelectorAll('.product-addon__checkbox:checked').forEach(function (cb) {
      var p = {};
      p[PROP_PARENT] = String(pid);
      p[PROP_MARKER] = 'true';
      out.push({ id: parseInt(cb.dataset.variantId, 10), quantity: 1, properties: p });
    });
    return out;
  }

  function installFetchHook() {
    var real = window.fetch;
    window.__addonRealFetch = real;

    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource : (resource && resource.url || '');
      var method = ((init && init.method) || 'GET').toUpperCase();
      var isAdd = method === 'POST' && url.indexOf('/cart/add') !== -1;

      if (isAdd) {
        var addons = readCheckedAddons();
        if (addons.length > 0) {
          return real.apply(this, arguments).then(function (res) {
            if (!res.ok) return res;
            var clone = res.clone();
            real.call(window, root() + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: addons })
            })
              .then(function () { refreshNotificationWithAddons(); })
              .catch(function () {});
            return clone;
          });
        }
      }

      return real.apply(this, arguments);
    };
  }

  /* ── Disable quantity selectors for add-on products in the cart ──────── */

  /**
   * Fetches the cart and disables the quantity input + +/- buttons
   * for any line item that is an add-on product (_is_addon = "true").
   * Called after cart renders/updates.
   */
  function disableAddonQuantitySelectors() {
    realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var addonKeys = {};
        cart.items.forEach(function (it) {
          if (it.properties && it.properties[PROP_MARKER] === 'true') {
            addonKeys[it.key] = true;
            addonKeys['variant-' + it.variant_id] = true;
            addonKeys['index-' + it.line] = true;
          }
        });

        // Target quantity-input custom elements (Dawn theme pattern)
        document.querySelectorAll('quantity-input').forEach(function (qtyEl) {
          var input = qtyEl.querySelector('input[type="number"], input[name="updates[]"], input.quantity__input');
          if (!input) return;

          var row = input.closest('[data-key], [data-cart-item-key], tr, li, .cart-item');
          var isAddon = false;

          if (row) {
            var key = row.dataset.key || row.dataset.cartItemKey;
            if (key && addonKeys[key]) isAddon = true;

            if (!isAddon) {
              var variantId = row.dataset.variantId;
              if (variantId && addonKeys['variant-' + variantId]) isAddon = true;
            }

            if (!isAddon) {
              var idx = row.dataset.index;
              if (idx && addonKeys['index-' + idx]) isAddon = true;
            }
          }

          if (isAddon) {
            input.setAttribute('disabled', 'disabled');
            input.style.pointerEvents = 'none';
            input.style.opacity = '0.5';
            qtyEl.querySelectorAll('button').forEach(function (btn) {
              btn.setAttribute('disabled', 'disabled');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.5';
            });
          }
        });

        // Also handle plain inputs not wrapped in quantity-input
        document.querySelectorAll('input[name="updates[]"]').forEach(function (input) {
          if (input.closest('quantity-input')) return;
          var row = input.closest('[data-key], [data-cart-item-key], tr, li, .cart-item');
          var isAddon = false;

          if (row) {
            var key = row.dataset.key || row.dataset.cartItemKey;
            if (key && addonKeys[key]) isAddon = true;

            if (!isAddon) {
              var variantId = row.dataset.variantId;
              if (variantId && addonKeys['variant-' + variantId]) isAddon = true;
            }
          }

          if (isAddon) {
            input.setAttribute('disabled', 'disabled');
            input.style.pointerEvents = 'none';
            input.style.opacity = '0.5';
            var container = input.parentElement;
            if (container) {
              container.querySelectorAll('button').forEach(function (btn) {
                btn.setAttribute('disabled', 'disabled');
                btn.style.pointerEvents = 'none';
                btn.style.opacity = '0.5';
              });
            }
          }
        });
      })
      .catch(function () {});
  }

  /* ── DOM observer (safety net) ───────────────────────────────────────── */

  function installObserver() {
    var target =
      document.getElementById('shopify-section-cart-items') ||
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer') ||
      document.querySelector('cart-items') ||
      document.querySelector('.cart-items');
    if (target) {
      var mo = new MutationObserver(function () {
        _lastCartToken = '';
        setTimeout(enforce, 600);
        setTimeout(checkCartForAddonOnly, 700);
        setTimeout(disableAddonQuantitySelectors, 800);
      });
      mo.observe(target, { childList: true, subtree: true });
    }
  }

  /* ── Polling (safety net on /cart page) ─────────────────────────────── */

  function installPoll() {
    if (window.location.pathname.indexOf('/cart') !== -1) {
      setInterval(function () {
        enforce();
        checkCartForAddonOnly();
        disableAddonQuantitySelectors();
      }, 2000);
    }
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  function isAddonProduct(item) {
    var title = (item.product_title || item.title || '').trim();
    return title.indexOf('Add On:') === 0;
  }

  function ensureWarningStyles() {
    var STYLE_ID = 'addon-only-cart-style';
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '#addon-only-cart-warning {' +
      '  background: #fff3cd;' +
      '  border: 1px solid #ffc107;' +
      '  border-radius: 4px;' +
      '  color: #664d03;' +
      '  font-size: 14px;' +
      '  margin: 12px 0;' +
      '  padding: 12px 16px;' +
      '  text-align: center;' +
      '}';
    document.head.appendChild(style);
  }

  function checkCartForAddonOnly() {
    realFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        if (items.length === 0) {
      document.querySelectorAll('#addon-only-cart-warning').forEach(function (el) { el.remove(); });
      var checkoutBtnsEmpty = document.querySelectorAll('[name="checkout"], button#checkout, .cart__checkout-button');
      checkoutBtnsEmpty.forEach(function (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.style.opacity = '';
        btn.style.cursor = '';
        btn.style.pointerEvents = '';
      });
      return;
    }

        var hasNonAddon = items.some(function (it) { return !isAddonProduct(it); });
        var checkoutBtns = document.querySelectorAll(
          '[name="checkout"], button#checkout, .cart__checkout-button'
        );

        if (!hasNonAddon) {
          ensureWarningStyles();
          checkoutBtns.forEach(function (btn) {
            var container = btn.closest('.cart__ctas') || btn.parentNode;
            if (container && !container.querySelector('#addon-only-cart-warning')) {
              var warn = document.createElement('div');
              warn.id = 'addon-only-cart-warning';
              warn.textContent =
                'Add-on products cannot be purchased alone. ' +
                'Please add a kit to your cart before checking out.';
              container.insertBefore(warn, btn);
            }
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.style.opacity = '0.5';
            btn.style.cursor = 'not-allowed';
            btn.style.pointerEvents = 'none';
          });
        } else {
          document.querySelectorAll('#addon-only-cart-warning').forEach(function (el) {
            el.remove();
          });
          checkoutBtns.forEach(function (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            btn.style.opacity = '';
            btn.style.cursor = '';
            btn.style.pointerEvents = '';
          });
        }
      })
      .catch(function () {});
  }

  function boot() {
    installFetchHook();
    installRemoveListener();
    initProductPage();
    installObserver();
    installPoll();
    enforce();
    checkCartForAddonOnly();
    disableAddonQuantitySelectors();
  }

  if (document.readyState === 'loading') {
    installFetchHook();
    installRemoveListener();
    document.addEventListener('DOMContentLoaded', function () {
      initProductPage();
      installObserver();
      installPoll();
      enforce();
      checkCartForAddonOnly();
      disableAddonQuantitySelectors();
    });
  } else {
    boot();
  }
})();